"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import {
  AlertTriangleIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CalendarRangeIcon,
  CheckIcon,
  ChevronRightIcon,
  ClockIcon,
  DownloadIcon,
  FileTextIcon,
  LandmarkIcon,
  PieChartIcon,
  PiggyBankIcon,
  Undo2Icon,
  UserIcon,
  ZapIcon,
} from "lucide-react"

import { Badge, Button, CopyButton, DetailPageHeader, UserAvatar, cn } from "@ibada/ui"

import {
  FinanceTransactionKind,
  FinanceTransactionType,
  type FinanceEntityInvestigation,
  type FinanceTransactionInvestigationFacts,
  type MoneyString,
} from "@ibada/shared-types/finance"
import {
  InvestigationTone,
  type InvestigationRef,
  type InvestigationTimelineEvent,
} from "@ibada/shared-types/investigation"

import { HomeTile } from "@/features/dashboard/home-d4/components/home-tile"
import { toBadgeVariant } from "@/features/investigation"
import { EXPENSE_TEXT_CLASS, INCOME_TEXT_CLASS } from "@/features/finance/overview/church-f3/components/church-tile-parts"
import {
  InvestigationCheckStatus,
  InvestigationLinkType,
  SheetAmountDirection,
  amountDirection,
  balancePill,
  buildInvestigationChecks,
  buildInvestigationLinks,
  openCheckCount,
  transactionPills,
  type InvestigationCheck,
  type InvestigationCheckLinks,
  type InvestigationLinkRow,
  type SheetPill,
} from "@/features/finance/lib/finance-investigation-sheet"
import { paymentMethodLabel } from "@/features/finance/lib/finance-investigation-visuals"
import { useTranslations } from "@/i18n"

export type InvestigationSheetLinks = InvestigationCheckLinks & {
  /** The general ledger of the transaction's account, when there is one. */
  ledger: string | null
}

export type FinanceInvestigationSheetProps = {
  /** The investigation, already localized (labels, account names, timeline titles). */
  investigation: FinanceEntityInvestigation
  /** Treasury accounts a ledger entry names without a ref (from the raw context strip). */
  unlinkedAccounts?: readonly string[]
  /** Formats a canonical decimal string in the investigation's currency, in the reader's locale. */
  formatAmount: (amount: MoneyString) => string
  formatDateTime: (iso: string) => string
  formatDay: (iso: string) => string
  resolveHref: (ref: InvestigationRef) => string
  links: InvestigationSheetLinks
  /** The shareable URL "Partager" copies. */
  shareUrl: string
  onBack?: () => void
  backLabel?: string
  /** The drawer peek: header, the amount card, checks and entries, one column. */
  compact?: boolean
  /**
   * "Télécharger le PDF" (the API prints the sheet). Offered for a transaction only: a ledger entry has no
   * transaction facts, so there is no document for it and the button is not shown.
   */
  pdf?: { onDownload: () => void; isPending: boolean } | null
}

const CARD_CLASS = "flex flex-col gap-2.5 rounded-2xl border border-border/60 bg-card px-[18px] py-4"

const AMOUNT_CLASS: Record<SheetAmountDirection, string> = {
  [SheetAmountDirection.In]: INCOME_TEXT_CLASS,
  [SheetAmountDirection.Out]: EXPENSE_TEXT_CLASS,
  [SheetAmountDirection.Neutral]: "text-foreground",
}

const CHECK_TONE: Record<InvestigationCheckStatus, { box: string; Icon: React.ComponentType<{ className?: string }> }> = {
  [InvestigationCheckStatus.Ok]: { box: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", Icon: CheckIcon },
  [InvestigationCheckStatus.Warning]: { box: "bg-amber-500/10 text-amber-700 dark:text-amber-300", Icon: AlertTriangleIcon },
  [InvestigationCheckStatus.Todo]: { box: "bg-blue-500/10 text-blue-700 dark:text-blue-300", Icon: ClockIcon },
}

const LINK_ICON: Record<InvestigationLinkType, React.ComponentType<{ className?: string }>> = {
  [InvestigationLinkType.Donor]: UserIcon,
  [InvestigationLinkType.Member]: UserIcon,
  [InvestigationLinkType.Fund]: PiggyBankIcon,
  [InvestigationLinkType.Budget]: PieChartIcon,
  [InvestigationLinkType.Account]: LandmarkIcon,
  [InvestigationLinkType.AccountingLine]: BookOpenIcon,
  [InvestigationLinkType.Period]: CalendarDaysIcon,
  [InvestigationLinkType.Program]: CalendarRangeIcon,
  [InvestigationLinkType.Activity]: ZapIcon,
  [InvestigationLinkType.Reverses]: Undo2Icon,
  [InvestigationLinkType.ReversedBy]: Undo2Icon,
  [InvestigationLinkType.Entry]: FileTextIcon,
}

const JOURNAL_DEBIT = "DEBIT"

/** How the API displays the workspace system branch (`branchDisplayName`). */
const PARISH_DISPLAY_NAME = "Parish"

type Fact = { key: string; label: string; value: React.ReactNode }

const TONES = Object.values(InvestigationTone) as string[]
const toInvestigationTone = (tone: string): InvestigationTone =>
  TONES.includes(tone) ? (tone as InvestigationTone) : InvestigationTone.Default

function Pills({ pills }: { pills: SheetPill[] }) {
  return (
    <>
      {pills.map((pill, index) => (
        <Badge key={`${pill.label}-${index}`} variant={toBadgeVariant(pill.tone)} data-testid='investigation-pill'>
          {pill.label}
        </Badge>
      ))}
    </>
  )
}

function AmountCard({
  amount,
  amountClass,
  kindLine,
  facts,
  columns,
}: {
  amount: string | null
  amountClass: string
  kindLine: string
  facts: Fact[]
  columns: 2 | 3
}) {
  return (
    <section className={CARD_CLASS} data-testid='investigation-amount-card'>
      {amount ? (
        <div className='flex flex-wrap items-baseline gap-x-2.5 gap-y-1'>
          <span className={cn("text-[2.125rem] leading-none font-extrabold tracking-tight tabular-nums", amountClass)}>
            {amount}
          </span>
          <span className='text-[13px] text-muted-foreground'>{kindLine}</span>
        </div>
      ) : null}
      <dl className={cn("grid gap-2", columns === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2")}>
        {facts.map((fact) => (
          <div key={fact.key} data-testid={`investigation-fact-${fact.key}`} className='flex min-w-0 flex-col gap-0.5 rounded-[10px] bg-muted/60 px-3 py-2.5'>
            <dt className='text-[11.5px] text-muted-foreground'>{fact.label}</dt>
            <dd className='text-sm font-bold break-words'>{fact.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function EntriesCard({
  investigation,
  formatAmount,
  resolveHref,
  ledgerHref,
}: {
  investigation: FinanceEntityInvestigation
  formatAmount: (amount: MoneyString) => string
  resolveHref: (ref: InvestigationRef) => string
  ledgerHref: string | null
}) {
  const t = useTranslations("finance.audit")
  const tLines = useTranslations("investigation.accounting")
  const impact = investigation.accountingImpact
  if (!impact) return null
  return (
    <HomeTile
      title={t("sheet.sections.entries")}
      link={ledgerHref ? { label: t("sheet.sections.ledger"), href: ledgerHref } : null}
      className='h-auto'
      testId='investigation-entries'
    >
      <div className='overflow-hidden rounded-xl border border-border/60 text-[13px]'>
        <div className='grid grid-cols-[1fr_7rem_7rem] bg-muted/40 px-3 py-2 text-xs font-bold text-muted-foreground'>
          <span>{tLines("account")}</span>
          <span className='text-right'>{tLines("debit")}</span>
          <span className='text-right'>{tLines("credit")}</span>
        </div>
        {impact.lines.map((line, index) => {
          const isDebit = line.direction === JOURNAL_DEBIT
          const href = resolveHref({ entity: "accountingAccount", id: line.accountingAccountId, label: line.name, clickable: true })
          return (
            <div key={`${line.accountingAccountId}-${index}`} className='grid grid-cols-[1fr_7rem_7rem] border-t border-border/60 px-3 py-2'>
              <Link to={href} className='min-w-0 truncate hover:text-primary'>
                <span className='mr-1.5 text-muted-foreground'>{line.code}</span>
                {line.name}
              </Link>
              <span className='text-right font-bold tabular-nums'>{isDebit ? formatAmount(line.amount) : ""}</span>
              <span className='text-right font-bold tabular-nums'>{isDebit ? "" : formatAmount(line.amount)}</span>
            </div>
          )
        })}
        <div className='grid grid-cols-[1fr_7rem_7rem] border-t border-border/60 bg-muted/20 px-3 py-2 text-muted-foreground'>
          <span className={cn("text-xs font-bold", impact.balanced ? "text-emerald-700 dark:text-emerald-300" : "text-destructive")}>
            {impact.balanced ? t("sheet.status.balanced") : t("sheet.status.unbalanced")}
          </span>
          <span className='text-right font-semibold text-foreground tabular-nums'>{formatAmount(impact.totalDebit)}</span>
          <span className='text-right font-semibold text-foreground tabular-nums'>{formatAmount(impact.totalCredit)}</span>
        </div>
      </div>
    </HomeTile>
  )
}

function LinkedCard({ rows, resolveHref }: { rows: InvestigationLinkRow[]; resolveHref: (ref: InvestigationRef) => string }) {
  const t = useTranslations("finance.audit")
  return (
    <HomeTile title={t("sheet.sections.linked")} className='h-auto' testId='investigation-linked'>
      {rows.length === 0 ? (
        <p className='text-[13px] text-muted-foreground'>{t("sheet.links.empty")}</p>
      ) : (
        <ul className='grid grid-cols-1 gap-x-5 sm:grid-cols-2'>
          {rows.map((row) => {
            const Icon = LINK_ICON[row.type]
            const body = (
              <>
                <span className='inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'>
                  <Icon className='size-3.5' />
                </span>
                <span className='w-16 shrink-0 text-xs text-muted-foreground'>{t(`sheet.links.${row.type}`)}</span>
                <span className='min-w-0 flex-1 truncate text-[13px] font-bold'>{row.value}</span>
                {row.ref?.clickable ? <ChevronRightIcon aria-hidden className='size-3.5 shrink-0 text-muted-foreground' /> : null}
              </>
            )
            return (
              <li key={`${row.type}-${row.ref?.id ?? row.value}`} data-testid='investigation-link-row' className='border-t border-border/60'>
                {row.ref?.clickable ? (
                  <Link to={resolveHref(row.ref)} className='flex items-center gap-2.5 py-1.5 hover:text-primary'>
                    {body}
                  </Link>
                ) : (
                  <div className='flex items-center gap-2.5 py-1.5'>{body}</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </HomeTile>
  )
}

function ChecksCard({ checks }: { checks: InvestigationCheck[] }) {
  const t = useTranslations("finance.audit")
  const open = openCheckCount(checks)
  return (
    <HomeTile
      title={t("sheet.sections.checks")}
      subtitle={
        open === 0
          ? t("sheet.sections.allClear")
          : open === 1
            ? t("sheet.sections.openPoint")
            : t("sheet.sections.openPoints", { count: open })
      }
      className='h-auto'
      testId='investigation-checks'
    >
      <ul className='flex flex-col'>
        {checks.map((check) => {
          const tone = CHECK_TONE[check.status]
          return (
            <li
              key={check.id}
              data-testid={`investigation-check-${check.id}`}
              data-status={check.status}
              className='flex items-center gap-2.5 border-t border-border/60 py-2'
            >
              <span className={cn("inline-flex size-[26px] shrink-0 items-center justify-center rounded-lg", tone.box)}>
                <tone.Icon className='size-3.5' />
              </span>
              <span className='flex min-w-0 flex-1 flex-col'>
                <span className='text-[13px] font-bold'>{check.title}</span>
                <span className='text-xs text-muted-foreground'>{check.detail}</span>
              </span>
              {check.action ? (
                <Link to={check.action.href} className='shrink-0 text-[12.5px] font-bold text-primary hover:underline'>
                  {check.action.label}
                </Link>
              ) : null}
            </li>
          )
        })}
      </ul>
    </HomeTile>
  )
}

function HistoryCard({ timeline, formatDay, formatDateTime }: { timeline: InvestigationTimelineEvent[]; formatDay: (iso: string) => string; formatDateTime: (iso: string) => string }) {
  const t = useTranslations("finance.audit")
  return (
    <HomeTile title={t("sheet.sections.history")} className='h-auto' testId='investigation-history'>
      {timeline.length === 0 ? (
        <p className='text-[13px] text-muted-foreground'>{t("sheet.history.none")}</p>
      ) : (
        <ol className='flex flex-col'>
          {timeline.map((event) => (
            <li key={event.id} data-testid='investigation-history-row' className='flex gap-2.5 border-t border-border/60 py-1.5 text-[12.5px]'>
              <time dateTime={event.timestamp} title={formatDateTime(event.timestamp)} className='w-[5.5rem] shrink-0 text-muted-foreground'>
                {formatDay(event.timestamp)}
              </time>
              <span className='flex min-w-0 flex-col'>
                <span>
                  <span className='font-semibold'>{event.title}</span>
                  {event.actor ? <span className='text-muted-foreground'> {t("sheet.history.by", { name: event.actor.name })}</span> : null}
                </span>
                {event.description ? <span className='text-muted-foreground'>{event.description}</span> : null}
                {event.details?.length ? (
                  <span className='text-muted-foreground'>
                    {event.details.map((detail) => `${detail.label} ${detail.value}`).join(" · ")}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      )}
    </HomeTile>
  )
}

/** The fact grid of a transaction: when it happened, when and by whom it was entered, how, where and what. */
function transactionFactRows(
  facts: FinanceTransactionInvestigationFacts,
  t: (key: string, values?: Record<string, string | number>) => string,
  parishLabel: string,
  formatDateTime: (iso: string) => string,
  formatDay: (iso: string) => string,
): Fact[] {
  const none = t("sheet.facts.none")
  const recordedOn = t("sheet.facts.recordedOn", { date: formatDay(facts.recordedAt) })
  // Who entered it is a person, so it gets its own tile with a face, not a name glued after a date.
  const recordedBy: React.ReactNode = facts.recordedBy ? (
    <span className='flex items-center gap-2'>
      <UserAvatar name={facts.recordedBy.name} className='size-6' />
      <span className='flex min-w-0 flex-col'>
        <span className='truncate'>{facts.recordedBy.name}</span>
        <span className='text-muted-foreground text-xs font-medium'>{recordedOn}</span>
      </span>
    </span>
  ) : (
    recordedOn
  )
  const memberLabel = facts.type === FinanceTransactionType.Income ? t("sheet.facts.donor") : t("sheet.facts.member")
  return [
    { key: "occurredAt", label: t("sheet.facts.occurredAt"), value: formatDateTime(facts.occurredAt) },
    { key: "recordedBy", label: t("sheet.facts.recordedBy"), value: recordedBy },
    { key: "member", label: memberLabel, value: facts.member?.name ?? none },
    { key: "paymentMethod", label: t("sheet.facts.paymentMethod"), value: paymentMethodLabel(facts.paymentMethod, t) ?? none },
    { key: "reference", label: t("sheet.facts.reference"), value: facts.reference ?? none },
    { key: "description", label: t("sheet.facts.description"), value: facts.description ?? none },
  ]
}

/**
 * The investigation as a fact sheet (option I2): what it is and how much in words, the key facts at a glance,
 * the entries, what it is linked to, and on the right what needs checking and what happened. A transaction
 * gets the full sheet; a ledger entry the same chrome over what it has (no payment, receipt or reconciliation).
 */
export function FinanceInvestigationSheet({
  investigation,
  unlinkedAccounts = [],
  formatAmount,
  formatDateTime,
  formatDay,
  resolveHref,
  links,
  shareUrl,
  onBack,
  backLabel,
  compact = false,
  pdf = null,
}: FinanceInvestigationSheetProps) {
  const t = useTranslations("finance.audit")
  const tCommon = useTranslations("common")
  const facts = investigation.transactionFacts ?? null
  const { header, accountingImpact } = investigation
  const balanced = accountingImpact ? accountingImpact.balanced : null

  const amount = header.amount != null ? formatAmount(header.amount) : null
  const accountName = facts?.accountingAccount
    ? facts.accountingAccount.name
    : null
  const kind = facts
    ? facts.kind === FinanceTransactionKind.Reversal
      ? t("sheet.status.reversal")
      : t(`type.${facts.type}`)
    : header.title
  const title = amount ? t("sheet.titleOf", { kind, amount }) : kind
  const direction = amountDirection(facts?.type)

  const pills: SheetPill[] = facts
    ? [
        // A transfer's own badge names its counterparty ("Transfert sortant · Paroisse"): kept, it is the fact.
        ...header.badges.filter((badge) => badge.label.includes(" · ")).map((badge) => ({ label: badge.label, tone: toInvestigationTone(badge.tone) })),
        ...transactionPills(facts, balanced, t),
      ]
    : [
        // A ledger entry leads with whether it still stands; the server's first badge carries that as its tone.
        header.badges[0]?.tone === InvestigationTone.Destructive
          ? { label: t("sheet.status.reversed"), tone: InvestigationTone.Destructive }
          : { label: t("sheet.status.posted"), tone: InvestigationTone.Success },
        ...(balanced === null ? [] : [balancePill(balanced, t)]),
      ]

  const subtitle = facts
    ? [facts.reference, facts.description, facts.branchIsParish ? tCommon("scope.parish") : facts.branchName]
        .filter(Boolean)
        .join(" · ")
    : header.subtitle ?? ""

  const kindLine = facts
    ? [accountName ?? null, direction === SheetAmountDirection.Neutral ? null : t(`sheet.direction.${direction}`)]
        .filter(Boolean)
        .join(" · ")
    : header.title

  const factRows: Fact[] = facts
    ? transactionFactRows(facts, t, tCommon("scope.parish"), formatDateTime, formatDay)
    : investigation.summary
        .filter((field) => field.value)
        .map((field) => ({
          key: field.label,
          label: field.label,
          // The server names the system branch "Parish"; say it in the reader's language.
          value: field.value === PARISH_DISPLAY_NAME ? tCommon("scope.parish") : (field.value ?? ""),
        }))

  const checks = buildInvestigationChecks(investigation, facts, links, t, { formatDate: formatDay, formatMoney: formatAmount })
  const linkRows = buildInvestigationLinks(investigation, facts, unlinkedAccounts)

  const share = (
    <CopyButton value={shareUrl} variant='outline' size='sm' iconPosition='start'>
      {t("sheet.share")}
    </CopyButton>
  )

  if (compact) {
    return (
      <div className='flex flex-col gap-3' data-testid='investigation-sheet-compact'>
        <div className='flex flex-col gap-1'>
          <div className='flex items-start justify-between gap-3'>
            <h2 className='text-lg leading-tight font-bold'>{title}</h2>
            {share}
          </div>
          {subtitle ? <p className='text-[13px] text-muted-foreground'>{subtitle}</p> : null}
          <div className='flex flex-wrap items-center gap-1.5 pt-1'>
            <Pills pills={pills} />
          </div>
        </div>
        <AmountCard amount={amount} amountClass={AMOUNT_CLASS[direction]} kindLine={kindLine} facts={factRows} columns={2} />
        {checks.length > 0 ? <ChecksCard checks={checks} /> : null}
        <EntriesCard investigation={investigation} formatAmount={formatAmount} resolveHref={resolveHref} ledgerHref={links.ledger} />
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-3.5' data-testid='investigation-sheet'>
      <DetailPageHeader
        title={title}
        description={subtitle || undefined}
        onBack={onBack}
        backLabel={backLabel}
        meta={pills.length ? <Pills pills={pills} /> : undefined}
        actions={
          facts && pdf ? (
            <div className='flex items-center gap-2'>
              {share}
              <Button
                variant='outline'
                size='sm'
                loading={pdf.isPending}
                onClick={pdf.onDownload}
                data-testid='investigation-download-pdf'
              >
                {pdf.isPending ? null : <DownloadIcon />}
                {t("sheet.downloadPdf")}
              </Button>
            </div>
          ) : (
            share
          )
        }
      />
      <div className='grid grid-cols-1 items-start gap-3.5 lg:grid-cols-[1.7fr_1fr]'>
        <div className='flex min-w-0 flex-col gap-3.5'>
          <AmountCard amount={amount} amountClass={AMOUNT_CLASS[direction]} kindLine={kindLine} facts={factRows} columns={3} />
          <EntriesCard investigation={investigation} formatAmount={formatAmount} resolveHref={resolveHref} ledgerHref={links.ledger} />
          <LinkedCard rows={linkRows} resolveHref={resolveHref} />
        </div>
        <div className='flex min-w-0 flex-col gap-3.5'>
          {checks.length > 0 ? <ChecksCard checks={checks} /> : null}
          <HistoryCard timeline={investigation.timeline} formatDay={formatDay} formatDateTime={formatDateTime} />
        </div>
      </div>
    </div>
  )
}
